import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import { toast } from "../utils/toast";

import { api } from "../api/api";
import AuthContext from "../contexts/AuthCtx";
import DashboardLayout from "../components/Dashboard/DashboardLayout";
import DeleteLetterModal from "../components/Dashboard/DeleteLetterModal";
import KanbanBoard from "../components/Dashboard/KanbanBoard";
import SendNowModal from "../components/Dashboard/SendNowModal";
import VitalStatusNotice from "../components/Dashboard/VitalStatusNotice";
import { categorizeLetters } from "../utils/letterBoard";
import { canSendLetterNow } from "../utils/letterSend";

async function fetchRecipientsForLetters(letters, token) {
  const recipientsByLetter = {};

  await Promise.all(
    letters.map(async (letter) => {
      try {
        const { data } = await api.get(`letter/getRecipients/${letter._id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        recipientsByLetter[letter._id] = data.recipients || [];
      } catch {
        recipientsByLetter[letter._id] = [];
      }
    })
  );

  return recipientsByLetter;
}

function Dashboard() {
  const AuthCtx = useContext(AuthContext);
  const navigate = useNavigate();
  const [board, setBoard] = useState({ draft: [], scheduled: [], sent: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [firstName, setFirstName] = useState("");
  const [hasSeenVitalStatusNotice, setHasSeenVitalStatusNotice] = useState(false);
  const [letterToDelete, setLetterToDelete] = useState(null);
  const [letterToSend, setLetterToSend] = useState(null);

  const loadBoard = useCallback(async () => {
    if (!AuthCtx.userToken) return;

    setIsLoading(true);
    try {
      const [lettersRes, userRes] = await Promise.all([
        api.get("letter/all", {
          headers: { Authorization: `Bearer ${AuthCtx.userToken}` },
        }),
        api.get("/auth/getUser", {
          headers: { Authorization: `Bearer ${AuthCtx.userToken}` },
        }),
      ]);

      const letters = lettersRes.data.letters || [];
      const recipientsByLetter = await fetchRecipientsForLetters(
        letters,
        AuthCtx.userToken
      );

      setBoard(categorizeLetters(letters, recipientsByLetter));
      setFirstName(userRes.data?.user?.user_info?.firstName || "");
      setHasSeenVitalStatusNotice(
        Boolean(userRes.data?.user?.hasSeenVitalStatusNotice)
      );
    } catch (err) {
      toast.error("Impossible de charger tes lettres.");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [AuthCtx.userToken]);

  useEffect(() => {
    loadBoard();
  }, [loadBoard]);

  const addLetterHandler = () => {
    api
      .post("letter/create", { title: "Sans titre" })
      .then((res) => navigate(`/letter/${res.data.id}`))
      .catch((err) => toast.error(err.response?.data?.message || "Erreur"));
  };

  const confirmDelete = async () => {
    if (!letterToDelete) return;

    try {
      await api.delete(`letter/delete/${letterToDelete._id}`);
      AuthCtx.updateHandler();
      setLetterToDelete(null);
      loadBoard();
      toast.success("Lettre supprimée.");
    } catch (err) {
      toast.error(err.response?.data?.message || "Erreur lors de la suppression.");
    }
  };

  const confirmSendNow = async (recipientIds) => {
    if (!letterToSend) return;

    if (!recipientIds?.length) {
      toast.error("Sélectionne au moins un destinataire.");
      return;
    }

    if (!canSendLetterNow(letterToSend, letterToSend.recipients || [])) {
      toast.error("Aucun destinataire disponible pour l'envoi.");
      setLetterToSend(null);
      return;
    }

    try {
      await api.post(`letter/sendNow/${letterToSend._id}`, { recipientIds });
      setLetterToSend(null);
      loadBoard();
      toast.success(
        recipientIds.length === 1
          ? "1 destinataire envoyé."
          : `${recipientIds.length} destinataires envoyés.`
      );
    } catch (err) {
      if (err.response?.status === 404) {
        toast.info(
          "L'envoi immédiat sera disponible dès que le backend sera mis à jour."
        );
        setLetterToSend(null);
        return;
      }
      toast.error(err.response?.data?.message || "Erreur lors de l'envoi.");
    }
  };

  const displayName = (firstName || "").trim().split(/\s+/)[0];
  const greeting = displayName
    ? `Qu'aimerais-tu lui dire, ${displayName} ?`
    : "Qu'aimerais-tu dire ?";

  return (
    <DashboardLayout onNewLetter={addLetterHandler} fillHeight>
      <div className="flex min-h-0 flex-1 flex-col lg:block lg:flex-none">
        {isLoading ? (
          <div className="flex flex-1 items-center justify-center lg:h-[60vh]">
            <ClipLoader color="#84a59d" size={40} />
          </div>
        ) : (
          <>
            <div className="mb-4 flex shrink-0 flex-col gap-4 sm:mb-6 sm:flex-row sm:items-start sm:justify-between lg:mb-8">
              <h1 className="max-w-3xl font-logo text-xl leading-snug text-black sm:whitespace-nowrap sm:text-2xl lg:text-[1.75rem]">
                {greeting}
              </h1>
              <VitalStatusNotice
                hasSeenVitalStatusNotice={hasSeenVitalStatusNotice}
              />
            </div>

            <KanbanBoard
              board={board}
              onAddDraft={addLetterHandler}
              onDelete={setLetterToDelete}
              onSendNow={setLetterToSend}
            />
          </>
        )}
      </div>

      <DeleteLetterModal
        open={Boolean(letterToDelete)}
        onClose={() => setLetterToDelete(null)}
        onConfirm={confirmDelete}
      />

      <SendNowModal
        open={Boolean(letterToSend)}
        recipients={letterToSend?.recipients || []}
        onClose={() => setLetterToSend(null)}
        onConfirm={confirmSendNow}
      />
    </DashboardLayout>
  );
}

export default Dashboard;
